// src/hooks/useStudentComms.js
//
// Pass 85.1B — MODERN student communications reads, scoped to the canonical participant + organisation (NEVER the bare
// Auth uid). Threads and notifications are queried `where organizationId == selectedOrg AND participantId == active
// participant` — so a student never runs a UID-global query, never blends organisations, and the unread total is
// tenant-safe. Every snapshot is scope-keyed `uid::org::participantId` and suppressed SYNCHRONOUSLY at render on a
// context switch (no stale row / no stale badge), and every hook fails closed (no subscription) unless all three of
// uid/org/participantId are present — so loading / error / needs_org / ambiguous perform ZERO reads.

import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const scopeKeyOf = (uid, org, pid) => (uid && org && pid ? `${uid}::${org}::${pid}` : '');

function useScopedCollection(collectionName, authUid, organizationId, participantId, sortFn) {
  const [snap, setSnap] = useState({ scopeKey: '', items: [], loading: true, error: null });
  const requestedScopeKey = scopeKeyOf(authUid, organizationId, participantId);
  useEffect(() => {
    setSnap({ scopeKey: requestedScopeKey, items: [], loading: !!requestedScopeKey, error: null });
    if (!requestedScopeKey) return undefined; // fail closed — no UID-global read, no cross-org read
    const q = query(collection(db, collectionName), where('organizationId', '==', organizationId), where('participantId', '==', participantId));
    const unsub = onSnapshot(
      q,
      (s) => setSnap({ scopeKey: requestedScopeKey, items: s.docs.map((d) => ({ id: d.id, ...d.data() })), loading: false, error: null }),
      (err) => setSnap({ scopeKey: requestedScopeKey, items: [], loading: false, error: err || new Error(`${collectionName}_read_failed`) }),
    );
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [collectionName, authUid, organizationId, participantId, requestedScopeKey]);
  return useMemo(() => {
    if (snap.scopeKey !== requestedScopeKey) return { loading: !!requestedScopeKey, error: null, items: [] };
    const items = sortFn ? [...snap.items].sort(sortFn) : snap.items;
    return { loading: snap.loading, error: snap.error, items };
  }, [snap, requestedScopeKey, sortFn]);
}

const byLatest = (a, b) => (b.lastMessageTimestamp?.seconds || 0) - (a.lastMessageTimestamp?.seconds || 0);
const byCreated = (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);

// Modern student threads for the selected participant + org (newest conversation first).
export function useStudentThreads(authUid, organizationId, participantId) {
  const { loading, error, items } = useScopedCollection('threads', authUid, organizationId, participantId, byLatest);
  return { loading, error, threads: items };
}

// Modern student notifications for the selected participant + org (newest first). Unread is derived ONLY from this
// tenant-scoped set (never a UID-global total).
export function useStudentNotifications(authUid, organizationId, participantId) {
  const { loading, error, items } = useScopedCollection('notifications', authUid, organizationId, participantId, byCreated);
  return { loading, error, notifications: items, unreadCount: items.filter((n) => !n.read).length };
}
